import React, { useState } from "react";
import { toast } from "sonner";
import { Loader2, CheckCircle2, Upload, FileText, Download } from "lucide-react";
import { DashboardLayout } from "../../components/DashboardLayout";
import { StatCard, StatusBadge, money } from "../../components/DashWidgets";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { useAuth } from "../../lib/auth";
import { LOAN_TYPES } from "../../lib/data";
import {
  createApplication, myApplications, myDocuments, uploadDocument, fileUrl, getToken, getUploadLink,
} from "../../lib/api";

const DOC_TYPES = ["Aadhar Card", "PAN Card", "Bank Statement", "Salary Slip", "GST Certificate", "ITR", "Other"];

export default function CustomerDashboard() {
  const { user } = useAuth();
  const [tab, setTab] = useState("overview");
  const [apps, setApps] = useState([]);
  const [docs, setDocs] = useState([]);
  const [loaded, setLoaded] = useState({});

  const load = async () => {
    const [a, d] = await Promise.all([myApplications(), myDocuments()]);
    setApps(a); setDocs(d);
    setLoaded({ done: true });
  };
  React.useEffect(() => { load(); }, []);

  return (
    <DashboardLayout tab={tab} setTab={setTab}>
      {tab === "overview" && <Overview apps={apps} docs={docs} setTab={setTab} />}
      {tab === "apply" && <ApplyLoan onDone={() => { load(); setTab("applications"); }} />}
      {tab === "applications" && <MyApplications apps={apps} />}
      {tab === "documents" && <MyDocuments docs={docs} apps={apps} reload={load} />}
    </DashboardLayout>
  );
}

function Overview({ apps, docs, setTab }) {
  const approved = apps.filter((a) => ["Approved", "Disbursed"].includes(a.status)).length;
  return (
    <div className="space-y-6" data-testid="customer-overview">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon="FileText" label="Applications" value={apps.length} testid="stat-apps" />
        <StatCard icon="CheckCircle2" label="Approved" value={approved} accent="green" testid="stat-approved" />
        <StatCard icon="FolderOpen" label="Documents" value={docs.length} testid="stat-docs" />
        <StatCard icon="Clock" label="In Process" value={apps.filter((a) => a.status === "In Process").length} testid="stat-inprocess" />
      </div>
      <div className="card-dark rounded-2xl p-6">
        <h3 className="font-heading text-lg font-bold text-white mb-2">Get started</h3>
        <p className="text-slate-400 text-sm mb-4">Apply for a new loan or upload your documents to speed up approval.</p>
        <div className="flex gap-3">
          <Button onClick={() => setTab("apply")} className="gold-gradient text-obsidian-900 font-bold rounded-full">Apply for Loan</Button>
          <Button onClick={() => setTab("documents")} variant="outline" className="border-gold/40 text-gold hover:bg-gold/10 rounded-full">Upload Documents</Button>
        </div>
      </div>
    </div>
  );
}

function ApplyLoan({ onDone }) {
  const [form, setForm] = useState({ loan_type: "", amount: "", tenure_months: "36", purpose: "" });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    if (!form.loan_type || !form.amount) { toast.error("Select loan type and amount"); return; }
    setLoading(true);
    try {
      await createApplication({ ...form, amount: Number(form.amount), tenure_months: Number(form.tenure_months) });
      toast.success("Application submitted!");
      onDone();
    } catch { toast.error("Could not submit"); } finally { setLoading(false); }
  };
  return (
    <div className="max-w-xl" data-testid="apply-loan">
      <h2 className="font-heading text-2xl font-bold text-white mb-5">Apply for a Loan</h2>
      <form onSubmit={submit} className="card-dark rounded-2xl p-6 space-y-4" data-testid="apply-form">
        <div>
          <Label className="text-slate-300 text-sm mb-1.5 block">Loan Type</Label>
          <Select value={form.loan_type} onValueChange={(v) => set("loan_type", v)}>
            <SelectTrigger data-testid="apply-loantype" className="bg-obsidian-900/60 border-white/10 h-11 text-white"><SelectValue placeholder="Select loan type" /></SelectTrigger>
            <SelectContent className="bg-obsidian-700 border-white/10 text-white">
              {LOAN_TYPES.map((t) => <SelectItem key={t} value={t} className="focus:bg-gold/20 focus:text-white">{t}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-slate-300 text-sm mb-1.5 block">Amount (₹)</Label>
            <Input data-testid="apply-amount" type="number" value={form.amount} onChange={(e) => set("amount", e.target.value)} className="bg-obsidian-900/60 border-white/10 h-11 text-white" />
          </div>
          <div>
            <Label className="text-slate-300 text-sm mb-1.5 block">Tenure (months)</Label>
            <Input data-testid="apply-tenure" type="number" value={form.tenure_months} onChange={(e) => set("tenure_months", e.target.value)} className="bg-obsidian-900/60 border-white/10 h-11 text-white" />
          </div>
        </div>
        <Textarea data-testid="apply-purpose" placeholder="Purpose of loan" value={form.purpose} onChange={(e) => set("purpose", e.target.value)} className="bg-obsidian-900/60 border-white/10 text-white" />
        <Button type="submit" disabled={loading} data-testid="apply-submit" className="w-full gold-gradient text-obsidian-900 font-bold h-11 rounded-full">
          {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Submit Application"}
        </Button>
      </form>
    </div>
  );
}

function MyApplications({ apps }) {
  return (
    <div data-testid="my-applications">
      <h2 className="font-heading text-2xl font-bold text-white mb-5">My Applications</h2>
      {apps.length === 0 ? (
        <p className="text-slate-400">No applications yet.</p>
      ) : (
        <div className="space-y-4">
          {apps.map((a) => (
            <div key={a.id} className="card-dark rounded-2xl p-5" data-testid={`app-row-${a.id}`}>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <div className="font-heading font-semibold text-white">{a.loan_type}</div>
                  <div className="font-mono-fin text-gold text-lg">{money(a.amount)}</div>
                </div>
                <StatusBadge status={a.status} />
              </div>
              {a.timeline && (
                <div className="mt-4 pt-4 border-t border-white/10 space-y-2">
                  {a.timeline.map((t, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs text-slate-400">
                      <span className="w-1.5 h-1.5 rounded-full bg-gold" />
                      <span className="text-slate-300 font-medium">{t.status}</span>
                      {t.note && <span>— {t.note}</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function MyDocuments({ docs, apps, reload }) {
  const [docType, setDocType] = useState("Aadhar Card");
  const [appId, setAppId] = useState("");
  const [uploading, setUploading] = useState(false);
  const [link, setLink] = useState("");
  React.useEffect(() => { getUploadLink().then((d) => setLink(`${window.location.origin}/u/${d.token}`)).catch(() => {}); }, []);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("doc_type", docType);
      if (appId) fd.append("application_id", appId);
      await uploadDocument(fd);
      toast.success("Document uploaded");
      reload();
    } catch { toast.error("Upload failed"); } finally { setUploading(false); e.target.value = ""; }
  };

  return (
    <div data-testid="my-documents">
      <h2 className="font-heading text-2xl font-bold text-white mb-5">My Documents</h2>
      <div className="card-dark rounded-2xl p-6 mb-6" data-testid="remote-upload-card">
        <h3 className="font-semibold text-white mb-1">Send from WhatsApp / Email / Phone</h3>
        <p className="text-slate-400 text-sm mb-3">Open this secure link on any device — or share it on WhatsApp — to upload documents straight to your profile.</p>
        <div className="flex gap-2 flex-wrap">
          <div className="flex-1 min-w-0 bg-obsidian-900/60 border border-white/10 rounded-lg px-3 py-2.5 text-slate-300 text-sm truncate" data-testid="remote-upload-link">{link || "…"}</div>
          <Button onClick={() => { const ok = () => toast.success("Link copied"); const bad = () => toast.error("Copy failed — select the link manually"); try { const p = navigator.clipboard?.writeText(link); if (p && p.then) p.then(ok).catch(bad); else ok(); } catch { bad(); } }} data-testid="copy-upload-link" className="gold-gradient text-obsidian-900 font-bold rounded-lg px-4">Copy</Button>
          <a href={`https://wa.me/919499799598?text=${encodeURIComponent("Hi Genesis, here is my document upload link: " + link)}`} target="_blank" rel="noreferrer" className="rounded-lg border border-gold/40 text-gold px-4 flex items-center text-sm" data-testid="whatsapp-upload-link">WhatsApp</a>
        </div>
      </div>
      <div className="card-dark rounded-2xl p-6 mb-6">
        <h3 className="font-semibold text-white mb-3">Upload a Document</h3>
        <div className="grid sm:grid-cols-2 gap-4 mb-4">
          <div>
            <Label className="text-slate-300 text-sm mb-1.5 block">Document Type</Label>
            <Select value={docType} onValueChange={setDocType}>
              <SelectTrigger data-testid="doc-type-select" className="bg-obsidian-900/60 border-white/10 h-11 text-white"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-obsidian-700 border-white/10 text-white">
                {DOC_TYPES.map((t) => <SelectItem key={t} value={t} className="focus:bg-gold/20 focus:text-white">{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-slate-300 text-sm mb-1.5 block">Link to Application (optional)</Label>
            <Select value={appId} onValueChange={setAppId}>
              <SelectTrigger data-testid="doc-app-select" className="bg-obsidian-900/60 border-white/10 h-11 text-white"><SelectValue placeholder="None" /></SelectTrigger>
              <SelectContent className="bg-obsidian-700 border-white/10 text-white">
                {apps.map((a) => <SelectItem key={a.id} value={a.id} className="focus:bg-gold/20 focus:text-white">{a.loan_type} — {money(a.amount)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <label className="flex items-center justify-center gap-2 border-2 border-dashed border-white/15 rounded-xl p-6 cursor-pointer hover:border-gold/40 transition-colors" data-testid="doc-upload-label">
          {uploading ? <Loader2 className="w-5 h-5 animate-spin text-gold" /> : <Upload className="w-5 h-5 text-gold" />}
          <span className="text-slate-300 text-sm">{uploading ? "Uploading..." : "Click to choose a file (PDF / image)"}</span>
          <input type="file" className="hidden" onChange={onFile} accept=".pdf,.jpg,.jpeg,.png,.webp" data-testid="doc-file-input" />
        </label>
      </div>

      {docs.length === 0 ? (
        <p className="text-slate-400">No documents uploaded yet.</p>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4">
          {docs.map((d) => (
            <a key={d.id} href={`${fileUrl(d.storage_path)}?auth=${getToken()}`} target="_blank" rel="noreferrer"
              className="card-dark rounded-xl p-4 flex items-center gap-3 hover:border-gold/40 transition-colors" data-testid={`doc-row-${d.id}`}>
              <div className="w-10 h-10 rounded-lg bg-gold/12 flex items-center justify-center shrink-0">
                <FileText className="w-5 h-5 text-gold" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-white text-sm font-medium truncate">{d.doc_type}</div>
                <div className="text-slate-500 text-xs truncate">{d.original_filename}</div>
              </div>
              <Download className="w-4 h-4 text-slate-400" />
            </a>
          ))}
        </div>
      )}
    </div>
  );
}
